import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import {
  CreateOrUpdateOfficeTermsRequest,
  IOfficeTermsAndConditions,
  OfficeService,
  PermissionSetName,
} from '@cai-services';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationProperties, Office, User } from '../../../core';
import { APP_PROPS } from '../../../cai-common.module';
import { OfficeUtil } from '../../../utils';
import { OfficeDeleteTermsModal } from '../modals/office-delete-terms-modal/office-delete-terms-modal.component';

@Component({
  selector: 'cai-office-terms-and-conditions',
  templateUrl: './office-terms-and-conditions.component.html',
  styleUrls: ['./office-terms-and-conditions.component.scss'],
  standalone: false,
})
export class OfficeTermsAndConditionsComponent implements OnInit, OnChanges {
  @Input() activeOffice: Office;
  @Input() currentUser: User;
  activeOfficeTermsAndConditions: IOfficeTermsAndConditions[];
  selectedTermsAndConditions: IOfficeTermsAndConditions | undefined;
  officeTermsAndConditionsForm: FormGroup;
  isEditing: boolean;
  iAmAdmin: boolean;
  canSetDefault: boolean;

  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly officeService: OfficeService,
    private readonly modalService: NgbModal,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.initializeActiveOfficeTermsAndConditions();
    this.initializeOfficeTermsAndConditionsForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('activeOffice')) {
      this.initializeActiveOfficeTermsAndConditions();
    }
  }

  private async initializeActiveOfficeTermsAndConditions(): Promise<void> {
    this.iAmAdmin = !!this.currentUser.officeAccesses.find(
      (oa) =>
        oa.office.id === this.activeOffice.id &&
        oa.roles.find((p) => p.name === PermissionSetName.OFFICE_ADMIN),
    );
    this.activeOfficeTermsAndConditions =
      await this.officeService.getOfficeTermsAndConditions(
        this.activeOffice.id,
      );

    this.canSetDefault = !this.activeOfficeTermsAndConditions.some(
      (terms) => terms.isDefault,
    );

    this.onSelectTermsAndConditions(
      this.activeOfficeTermsAndConditions[0]?.name,
    );

    this.cdr.detectChanges();
  }

  private initializeOfficeTermsAndConditionsForm(): void {
    this.officeTermsAndConditionsForm = new FormGroup({
      name: new FormControl(
        { value: this.selectedTermsAndConditions?.name ?? '', disabled: true },
        [Validators.required, Validators.maxLength(30)],
      ),
      isDefault: new FormControl({
        value: this.selectedTermsAndConditions?.isDefault ?? false,
        disabled: true,
      }),
      body: new FormControl({
        value: this.selectedTermsAndConditions?.body ?? '',
        disabled: true,
      }),
    });
  }

  startEditing(): void {
    this.isEditing = true;
    this.officeTermsAndConditionsForm.enable();
    if (
      this.selectedTermsAndConditions?.isDefault !== true &&
      this.canSetDefault === false
    ) {
      this.officeTermsAndConditionsForm.get('isDefault').disable();
    }
  }

  createTermsDraft(): void {
    this.selectedTermsAndConditions = undefined;
    this.initializeOfficeTermsAndConditionsForm();
    this.startEditing();
  }

  async saveChanges(): Promise<void> {
    try {
      const updatedName = this.officeTermsAndConditionsForm.get('name').value,
        oldName = this.selectedTermsAndConditions?.name ?? updatedName,
        updatedTerms: CreateOrUpdateOfficeTermsRequest = {
          name: updatedName,
          body: this.officeTermsAndConditionsForm.get('body').value,
          isDefault: this.officeTermsAndConditionsForm.get('isDefault').value,
        };
      await this.officeService.createOrUpdateOfficeTerms(
        this.activeOffice.id,
        oldName,
        updatedTerms,
      );

      const oldTerms = this.activeOfficeTermsAndConditions.find(
        (terms) => terms.name === oldName,
      );
      if (oldTerms === undefined) {
        this.activeOfficeTermsAndConditions.push(updatedTerms as any);
      } else {
        Object.assign(oldTerms, updatedTerms);
      }

      this.canSetDefault = !this.activeOfficeTermsAndConditions.some(
        (terms) => terms.isDefault,
      );

      this.onSelectTermsAndConditions(updatedName);
      this.stopEditing();
    } catch (e) {
      console.error(e);
    }
    this.cdr.detectChanges();
  }

  discardChanges(): void {
    if (this.isCreatingDraft) {
      this.discardDraft();
      return;
    }

    this.initializeOfficeTermsAndConditionsForm();
    this.stopEditing();
  }

  discardDraft(): void {
    this.onSelectTermsAndConditions(
      this.activeOfficeTermsAndConditions[0]?.name,
    );
    this.stopEditing();
  }

  stopEditing(): void {
    this.isEditing = false;
    this.officeTermsAndConditionsForm.disable();
  }

  async deleteOfficeTerms(): Promise<void> {
    if (this.isCreatingDraft) {
      this.discardDraft();
      return;
    }

    const deleteTermsModal = this.modalService.open(OfficeDeleteTermsModal, {
        centered: true,
        size: 'md',
      }),
      officeTermsToDelete = this.selectedTermsAndConditions;
    deleteTermsModal.componentInstance.activeOffice = this.activeOffice;
    deleteTermsModal.componentInstance.officeTermsToDelete =
      officeTermsToDelete;
    deleteTermsModal.componentInstance.modalRef = deleteTermsModal;

    try {
      await deleteTermsModal.result;
      this.activeOfficeTermsAndConditions =
        this.activeOfficeTermsAndConditions.filter(
          (terms) => terms.name !== officeTermsToDelete.name,
        );
      this.canSetDefault = !this.activeOfficeTermsAndConditions.some(
        (terms) => terms.isDefault,
      );
      this.onSelectTermsAndConditions(
        this.activeOfficeTermsAndConditions[0]?.name,
      );
      this.stopEditing();
    } catch (e) {
      console.error(e);
    }
    this.cdr.detectChanges();
  }

  get activeOfficeName(): string {
    return OfficeUtil.getOfficeName(this.activeOffice);
  }

  onSelectTermsAndConditions(termsAndConditionsName: string) {
    this.selectedTermsAndConditions = this.activeOfficeTermsAndConditions.find(
      (termsAndConditions) =>
        termsAndConditions.name === termsAndConditionsName,
    );
    this.initializeOfficeTermsAndConditionsForm();
  }

  controlHasError(controlName: string, validationType: string): boolean {
    const control = this.officeTermsAndConditionsForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType);
  }

  get isCreatingDraft(): boolean {
    return this.isEditing && this.selectedTermsAndConditions === undefined;
  }
}
